import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Phone, Mail, Globe, KeyRound, Eye, EyeOff, Search, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LEGACY_CATEGORIES = { parts: "Parts", inspection: "Inspection", garage: "Garage", other: "Other" };
const BADGE_PALETTE = [
  "text-[#0A84FF] border-[#0A84FF]/40 bg-[#0A84FF]/10",
  "text-emerald-600 dark:text-[#32D74B] border-emerald-600/40 dark:border-[#32D74B]/40 bg-emerald-600/10 dark:bg-[#32D74B]/10",
  "text-amber-600 dark:text-[#FFD60A] border-amber-600/40 dark:border-[#FFD60A]/40 bg-amber-600/10 dark:bg-[#FFD60A]/10",
  "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10",
  "text-purple-600 dark:text-purple-400 border-purple-600/40 bg-purple-600/10",
  "text-cyan-600 dark:text-cyan-400 border-cyan-600/40 bg-cyan-600/10",
  "text-pink-600 dark:text-pink-400 border-pink-600/40 bg-pink-600/10",
  "text-orange-600 dark:text-orange-400 border-orange-600/40 bg-orange-600/10",
];
const badgeStyle = (name) => BADGE_PALETTE[[...(name || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % BADGE_PALETTE.length];
const supBadges = (s) => (s.badges?.length ? s.badges : s.category ? [LEGACY_CATEGORIES[s.category] || s.category] : []);

const EMPTY = { name: "", category: "parts", badges: [], phone: "", email: "", website: "", username: "", password: "", notes: "" };

export default function SuppliersPage() {
  const { canEdit } = useAuth();
  const editable = canEdit("suppliers");
  const [suppliers, setSuppliers] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [activeBadges, setActiveBadges] = useState([]);
  const [newBadge, setNewBadge] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [revealId, setRevealId] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/suppliers").then((r) => setSuppliers(r.data));
  const loadBadges = () => api.get("/suppliers/badges").then((r) => setAllBadges(r.data));
  useEffect(() => { load(); loadBadges(); }, []);

  const openAdd = () => { setEditId(null); setForm(EMPTY); setNewBadge(""); setOpen(true); };
  const openEdit = (s) => {
    setEditId(s.id);
    const f = { ...EMPTY };
    Object.keys(EMPTY).forEach((k) => { f[k] = s[k] || (k === "category" ? "parts" : k === "badges" ? supBadges(s) : ""); });
    setNewBadge("");
    setForm(f);
    setOpen(true);
  };

  const toggleFormBadge = (b) => setForm((f) => ({ ...f, badges: f.badges.includes(b) ? f.badges.filter((x) => x !== b) : [...f.badges, b] }));

  const addNewBadge = async () => {
    const name = newBadge.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/suppliers/badges", { name });
      setNewBadge("");
      await loadBadges();
      setForm((f) => (f.badges.includes(data.name) ? f : { ...f, badges: [...f.badges, data.name] }));
      toast.success(data.created ? `Badge "${data.name}" created` : `Badge "${data.name}" already exists — selected`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "" && k !== "category") payload[k] = null; });
      if (editId) await api.put(`/suppliers/${editId}`, payload);
      else await api.post("/suppliers", payload);
      toast.success(editId ? "Supplier updated" : "Supplier added");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
    await api.delete(`/suppliers/${s.id}`);
    toast.success("Supplier deleted");
    load();
  };

  const toggleFilterBadge = (b) => setActiveBadges((a) => (a.includes(b) ? a.filter((x) => x !== b) : [...a, b]));

  const TEXT_FIELDS = [
    ["Company name *", "name", "supplier-name-input"],
    ["Phone", "phone", "supplier-phone-input"],
    ["Email", "email", "supplier-email-input"],
    ["Website", "website", "supplier-website-input"],
    ["Login username", "username", "supplier-username-input"],
    ["Login password", "password", "supplier-password-input"],
    ["Notes", "notes", "supplier-notes-input"],
  ];

  const shown = suppliers
    .filter((s) => [s.name, supBadges(s).join(" "), s.phone, s.email, s.website, s.notes].join(" ").toLowerCase().includes(filter.toLowerCase()))
    .filter((s) => activeBadges.length === 0 || supBadges(s).some((b) => activeBadges.includes(b)));

  const badgeCount = (b) => suppliers.filter((s) => supBadges(s).includes(b)).length;

  return (
    <div className="p-8" data-testid="suppliers-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">SUPPLIERS</h1>
          <p className="text-muted-foreground text-sm mt-1">Parts, inspection & garage companies — contacts and website logins in one place</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="supplier-search-input" placeholder="Search company, contact…" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-none bg-card border-border pl-9 w-64 h-10" />
          </div>
          {editable && (
          <Button data-testid="add-supplier-button" onClick={openAdd} className="rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-10">
            <Plus size={15} className="mr-1" /> Add supplier
          </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="badge-filter-bar">
        <button
          data-testid="badge-filter-all"
          onClick={() => setActiveBadges([])}
          className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold border transition-colors duration-150 ${activeBadges.length === 0 ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "bg-card text-muted-foreground border-border hover:text-foreground"}`}
        >
          All ({suppliers.length})
        </button>
        {allBadges.map((b) => (
          <button
            key={b}
            data-testid={`badge-filter-${b}`}
            onClick={() => toggleFilterBadge(b)}
            className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold border transition-colors duration-150 ${activeBadges.includes(b) ? "bg-foreground text-background border-foreground" : `${badgeStyle(b)} hover:opacity-80`}`}
          >
            {b} ({badgeCount(b)})
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-none text-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">{editId ? "EDIT SUPPLIER" : "ADD SUPPLIER"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-1.5"><Tag size={11} /> Badges — pick one or more</Label>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="form-badge-picker">
                {allBadges.map((b) => (
                  <button
                    key={b}
                    type="button"
                    data-testid={`form-badge-${b}`}
                    onClick={() => toggleFormBadge(b)}
                    className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold border transition-colors duration-150 ${form.badges.includes(b) ? "bg-foreground text-background border-foreground" : `${badgeStyle(b)} hover:opacity-80`}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Input
                  data-testid="new-badge-input"
                  placeholder="Add a new badge, e.g. Trailer"
                  value={newBadge}
                  onChange={(e) => setNewBadge(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNewBadge(); } }}
                  className="rounded-none bg-background border-border h-9"
                />
                <Button data-testid="add-badge-button" type="button" onClick={addNewBadge} variant="outline" className="rounded-none border-border uppercase text-[10px] tracking-widest font-bold h-9 bg-transparent shrink-0">
                  <Plus size={13} className="mr-1" /> Add badge
                </Button>
              </div>
            </div>
            {TEXT_FIELDS.map(([label, key, tid]) => (
              <div key={key}>
                <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{label}</Label>
                <Input data-testid={tid} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="mt-1.5 rounded-none bg-background border-border h-9" />
              </div>
            ))}
            <Button data-testid="save-supplier-button" onClick={save} className="w-full rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-11">{editId ? "Save changes" : "Save supplier"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.map((s) => (
          <div key={s.id} data-testid={`supplier-card-${s.name}`} className="border border-border bg-card p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-heading font-black text-xl tracking-tighter">{s.name}</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {supBadges(s).map((b) => (
                    <button key={b} data-testid={`supplier-badge-${s.name}-${b}`} onClick={() => toggleFilterBadge(b)} title={`Filter by ${b}`} className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold cursor-pointer hover:opacity-80 ${badgeStyle(b)}`}>{b}</button>
                  ))}
                </div>
              </div>
              {editable && (
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`edit-supplier-${s.name}`} onClick={() => openEdit(s)} className="text-muted-foreground/70 hover:text-foreground transition-colors duration-150"><Pencil size={14} /></button>
                <button data-testid={`delete-supplier-${s.name}`} onClick={() => remove(s)} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150"><Trash2 size={14} /></button>
              </div>
              )}
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
              {s.phone && <div className="flex items-center gap-2"><Phone size={13} /> {s.phone}</div>}
              {s.email && <div className="flex items-center gap-2"><Mail size={13} /> {s.email}</div>}
              {s.website && (
                <div className="flex items-center gap-2">
                  <Globe size={13} />
                  <a href={s.website.startsWith("http") ? s.website : `https://${s.website}`} target="_blank" rel="noreferrer" className="hover:underline hover:text-foreground truncate">{s.website}</a>
                </div>
              )}
              {s.notes && <div className="text-xs pt-1">{s.notes}</div>}
            </div>
            {(s.username || s.password) && (
              <div className="mt-4 pt-3 border-t border-border">
                <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground mb-1.5 flex items-center gap-1.5"><KeyRound size={11} /> Website login</div>
                <div className="text-sm font-mono">
                  {s.username && <div className="text-foreground/80">{s.username}</div>}
                  {s.password && (
                    <div className="flex items-center gap-2">
                      <span data-testid={`supplier-password-${s.name}`}>{revealId === s.id ? s.password : "••••••••"}</span>
                      <button data-testid={`toggle-password-${s.name}`} onClick={() => setRevealId(revealId === s.id ? null : s.id)} className="text-muted-foreground hover:text-foreground">
                        {revealId === s.id ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        {shown.length === 0 && (
          <div className="col-span-full border border-border bg-card p-12 text-center text-muted-foreground/70">{suppliers.length === 0 ? "No suppliers yet. Add companies like Bennetts, Halfords or MAN with their contacts and logins." : "No suppliers match your search or badge filter."}</div>
        )}
      </div>
    </div>
  );
}
